/**
 * Warehouse Workflow Engine
 * Enforces material state transitions with validation rules and audit trail.
 */

import type { MaterialState, MaterialEventAction, MaterialEvent } from "./types";

// ── State Transition Rules ──

const VALID_TRANSITIONS: Record<MaterialState, MaterialState[]> = {
  "Not Ordered": ["Ordered"],
  "Ordered": ["Received"],
  "Received": ["Allocated"],
  "Allocated": ["Installed"],
  "Installed": [],
};

const ACTION_TO_STATE: Record<Exclude<MaterialEventAction, "photo_attached" | "loadout_confirmed" | "materials_reviewed" | "materials_ready">, MaterialState> = {
  ordered: "Ordered",
  received: "Received",
  allocated: "Allocated",
  installed: "Installed",
};

export interface TransitionResult {
  allowed: boolean;
  reason?: string;
  newState?: MaterialState;
}

/** Check if a state transition is valid */
export function canTransition(currentState: MaterialState, targetState: MaterialState): TransitionResult {
  const allowed = VALID_TRANSITIONS[currentState];
  if (allowed.includes(targetState)) {
    return { allowed: true, newState: targetState };
  }
  return {
    allowed: false,
    reason: `Cannot move from "${currentState}" to "${targetState}". Must follow: Not Ordered → Ordered → Received → Allocated → Installed.`,
  };
}

/** Validate an action against current state */
export function validateAction(
  currentState: MaterialState,
  action: MaterialEventAction,
  loadoutConfirmed?: boolean,
): TransitionResult {
  if (action === "photo_attached") {
    return { allowed: true };
  }

  if (action === "loadout_confirmed") {
    if (currentState !== "Allocated") {
      return { allowed: false, reason: "Cannot confirm load-out unless materials are Allocated." };
    }
    return { allowed: true };
  }

  if (action === "installed") {
    if (currentState !== "Allocated") {
      return { allowed: false, reason: "Cannot mark as Installed unless materials are Allocated." };
    }
    if (!loadoutConfirmed) {
      return { allowed: false, reason: "Cannot mark as Installed until load-out is confirmed." };
    }
    return { allowed: true, newState: "Installed" };
  }

  const targetState = ACTION_TO_STATE[action as keyof typeof ACTION_TO_STATE];
  if (!targetState) {
    return { allowed: false, reason: `Unknown action: ${action}` };
  }

  return canTransition(currentState, targetState);
}

/** Create a material event record */
export function createMaterialEvent(
  jobId: string,
  sku: string,
  action: MaterialEventAction,
  performedBy: string,
  previousState?: MaterialState,
  newState?: MaterialState,
  notes?: string,
  photoUrl?: string,
): MaterialEvent {
  return {
    id: `me-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    jobId,
    sku,
    action,
    timestamp: new Date().toISOString(),
    performedBy,
    previousState,
    newState,
    notes,
    photoUrl,
  };
}

/** Get the next available action for a material state */
export function getNextAction(state: MaterialState): { action: MaterialEventAction; label: string } | null {
  switch (state) {
    case "Not Ordered": return { action: "ordered", label: "Order Materials" };
    case "Ordered": return { action: "received", label: "Mark as Received" };
    case "Received": return { action: "allocated", label: "Allocate to Job" };
    case "Allocated": return { action: "installed", label: "Mark as Installed" };
    case "Installed": return null;
  }
}

/** Get all available actions for a state */
export function getAvailableActions(state: MaterialState, loadoutConfirmed: boolean): Array<{ action: MaterialEventAction; label: string; enabled: boolean; reason?: string }> {
  const actions: Array<{ action: MaterialEventAction; label: string; enabled: boolean; reason?: string }> = [];

  if (state === "Not Ordered") {
    actions.push({ action: "ordered", label: "Order Materials", enabled: true });
  }
  if (state === "Ordered") {
    actions.push({ action: "received", label: "Mark as Received", enabled: true });
  }
  if (state === "Received") {
    actions.push({ action: "allocated", label: "Allocate to Job", enabled: true });
  }
  if (state === "Allocated") {
    actions.push({
      action: "loadout_confirmed",
      label: "Confirm Load-Out",
      enabled: true,
    });
    actions.push({
      action: "installed",
      label: "Mark as Installed",
      enabled: loadoutConfirmed,
      reason: loadoutConfirmed ? undefined : "Load-out must be confirmed first",
    });
  }

  return actions;
}

// ── Material State Display ──

export const MATERIAL_STATE_CONFIG: Record<MaterialState, { label: string; color: string; bgColor: string; borderColor: string }> = {
  "Not Ordered": { label: "Not Ordered", color: "text-destructive", bgColor: "bg-destructive/10", borderColor: "border-destructive/20" },
  "Ordered": { label: "Ordered", color: "text-warning", bgColor: "bg-warning/10", borderColor: "border-warning/20" },
  "Received": { label: "Received", color: "text-primary", bgColor: "bg-primary/10", borderColor: "border-primary/20" },
  "Allocated": { label: "Allocated", color: "text-accent-foreground", bgColor: "bg-accent/50", borderColor: "border-accent/30" },
  "Installed": { label: "Installed", color: "text-success", bgColor: "bg-success/10", borderColor: "border-success/20" },
};

// ── Integrity Analysis ──

export interface IntegrityException {
  type: "received_not_allocated" | "allocated_not_installed" | "installed_without_allocation";
  jobId: string;
  sku: string;
  description: string;
  severity: "warning" | "critical";
}

export function analyzeIntegrity(
  events: MaterialEvent[],
  todayStr: string,
): {
  receivedToday: number;
  allocatedToday: number;
  installedToday: number;
  exceptions: IntegrityException[];
} {
  const todayEvents = events.filter((e) => e.timestamp.startsWith(todayStr));

  const receivedToday = todayEvents.filter((e) => e.action === "received").length;
  const allocatedToday = todayEvents.filter((e) => e.action === "allocated").length;
  const installedToday = todayEvents.filter((e) => e.action === "installed").length;

  // Build current state map per job+sku
  const stateMap = new Map<string, MaterialState>();
  const allocationMap = new Map<string, boolean>();

  events.forEach((e) => {
    const key = `${e.jobId}::${e.sku}`;
    if (e.newState) stateMap.set(key, e.newState);
    if (e.action === "allocated") allocationMap.set(key, true);
  });

  const exceptions: IntegrityException[] = [];

  stateMap.forEach((state, key) => {
    const [jobId, sku] = key.split("::");

    if (state === "Received") {
      // Check if received more than 48h ago but not allocated
      const receivedEvent = events.filter((e) => `${e.jobId}::${e.sku}` === key && e.action === "received").pop();
      if (receivedEvent) {
        const hoursSince = (Date.now() - new Date(receivedEvent.timestamp).getTime()) / 3_600_000;
        if (hoursSince > 48) {
          exceptions.push({
            type: "received_not_allocated",
            jobId, sku,
            description: `${sku} received ${Math.round(hoursSince / 24)}d ago, not yet allocated`,
            severity: "warning",
          });
        }
      }
    }

    if (state === "Installed" && !allocationMap.has(key)) {
      exceptions.push({
        type: "installed_without_allocation",
        jobId, sku,
        description: `${sku} marked installed without allocation record`,
        severity: "critical",
      });
    }
  });

  return { receivedToday, allocatedToday, installedToday, exceptions };
}

/** Derive MaterialState from existing job materialReadiness field */
export function deriveStateFromReadiness(readiness: string): MaterialState {
  switch (readiness) {
    case "Not Ordered":
    case "Missing Items":
    case "Field Purchase Required":
      return "Not Ordered";
    case "Ordered":
    case "Partially Received":
      return "Ordered";
    case "Fully Received":
      return "Received";
    case "Staged for Job":
    case "Loaded for Crew":
      return "Allocated";
    default:
      return "Not Ordered";
  }
}
